// Context panel "Action items" view — real data, extracted by the same AI-2 summary pass
// that feeds the Summary panel.
//
// Source: the `action_items` array of the open recording's newest `kind: 'summary'` AI
// artifact (`GET /recordings/{id}/artifacts?kind=summary`). This reuses Summary's
// `useRecordingSummary` query (same query key), so switching between the two tabs never
// fetches twice. Like Summary — and unlike Assistant/Insights — there is deliberately NO
// `panelsEnabled()` gate: this is real data, not fabricated placeholder content.
//
// Owners come back from the model as whatever it heard in the transcript — frequently raw
// diarization labels (`spk_0`). When the open recording's transcript is loaded (shared
// `useTranscript` cache with RecordingDetail), labels with a `display_name` are mapped to it;
// anything else is shown as-is.
//
// Waiting for the summary is bounded (SUMMARY_WAIT_MS after transcription completes): past
// that, the panel says there is no summary and stops polling, instead of showing
// "Extracting action items…" and refetching every 15 s forever for recordings that will never
// get one (failed or skipped pass, pre-AI recordings).
//
// Items are read-only: there's no backend contract for completing an action item yet, so
// no checkbox is rendered (a local-only toggle would silently reset and read as saved).
import { useEffect, useState } from 'react';
import { ClipboardDocumentListIcon, CheckIcon } from '@heroicons/react/24/outline';
import { asSummaryContent } from '@scribe/core/types';
import type { RecordingStatus } from '@scribe/core/types';
import { Avatar } from '../ui';
import {
  formatDue,
  summaryWaitRemainingMs,
  toMarkdownChecklist,
  type ActionItem,
} from '../../lib/actionItems';
import { useRecording, useRecordingSummary, useTranscript } from '../../lib/queries';

function Empty({ children }: { children: string }) {
  return (
    <div className="action-items-view">
      <p className="empty-state">{children}</p>
    </div>
  );
}

export function ActionItems({
  recordingId,
  recordingStatus,
}: {
  recordingId?: string;
  recordingStatus?: RecordingStatus;
}) {
  const { data: recording } = useRecording(recordingId);
  const status = recordingStatus ?? recording?.status;
  // Which recording's summary wait has run out. Keyed by id so opening another recording
  // starts a fresh wait without a reset effect.
  const [waitExpiredFor, setWaitExpiredFor] = useState<string | null>(null);
  const waitExpired = recordingId !== undefined && waitExpiredFor === recordingId;
  // Passing no status once the wait is over is `useRecordingSummary`'s documented "don't
  // poll" input (queries.summary.test.tsx) — the query itself (and its cache key, shared
  // with Summary) is unchanged.
  const { data: artifacts, isError } = useRecordingSummary(
    recordingId,
    waitExpired ? undefined : status,
  );
  const { data: transcript } = useTranscript(status === 'completed' ? recordingId : undefined);
  const [copied, setCopied] = useState(false);

  const waitingForSummary =
    recordingId !== undefined &&
    status === 'completed' &&
    !isError &&
    artifacts !== undefined &&
    artifacts.length === 0 &&
    recording?.job_spec?.auto_summary !== false;
  const completedAt = recording?.transcription_completed_at;
  useEffect(() => {
    if (!waitingForSummary || recordingId === undefined) return;
    const timer = window.setTimeout(
      () => setWaitExpiredFor(recordingId),
      summaryWaitRemainingMs(completedAt, Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [waitingForSummary, recordingId, completedAt]);

  if (!recordingId) return <Empty>Open a recording to see its action items.</Empty>;
  if (status !== 'completed') {
    return <Empty>Action items are extracted after transcription completes.</Empty>;
  }
  if (isError && !artifacts) return <Empty>Couldn’t load action items. Try again shortly.</Empty>;
  if (!artifacts || artifacts.length === 0) {
    if (recording?.job_spec?.auto_summary === false) {
      return (
        <Empty>AI summary is turned off for this recording, so no action items were extracted.</Empty>
      );
    }
    if (waitExpired) {
      return <Empty>No summary for this recording, so there are no action items.</Empty>;
    }
    return <Empty>Extracting action items…</Empty>;
  }

  const names = new Map<string, string>();
  for (const s of transcript?.speakers ?? []) {
    if (s.display_name) names.set(s.label, s.display_name);
  }
  const items: ActionItem[] = asSummaryContent(artifacts[0]!.content)
    .action_items.filter((it) => it.item.trim() !== '')
    .map((it) => ({ ...it, owners: it.owners.map((o) => names.get(o) ?? o) }));

  if (items.length === 0) return <Empty>No action items found.</Empty>;

  async function copy() {
    try {
      await navigator.clipboard.writeText(toMarkdownChecklist(items));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="action-items-view">
      <div className="panel-section-title">
        Action items
        <span className="panel-section-count">{items.length}</span>
      </div>
      <ul className="action-item-list">
        {items.map((item, i) => (
          <li key={i} className="action-item">
            <div className="action-item-body">
              <div className="action-item-text">{item.item}</div>
              {(item.owners.length > 0 || item.due) && (
                <div className="action-item-meta">
                  {item.owners.map((owner, j) => (
                    // Owners can repeat (two labels mapped to one name, or the model listing
                    // someone twice), so the name alone is not a unique key.
                    <span key={`${owner}-${j}`} className="action-item-owner-group">
                      <Avatar name={owner} size="sm" />
                      <span className="action-item-owner">{owner}</span>
                    </span>
                  ))}
                  {item.due && <span className="due-chip">Due {formatDue(item.due)}</span>}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
      <div aria-live="polite">
        <button type="button" className="ghost sm action-items-copy" onClick={copy}>
          {copied ? (
            <CheckIcon width={16} height={16} aria-hidden="true" />
          ) : (
            <ClipboardDocumentListIcon width={16} height={16} aria-hidden="true" />
          )}
          {copied ? 'Copied' : 'Copy as markdown checklist'}
        </button>
      </div>
    </div>
  );
}
